import { queryExists } from '@/api/common'
import me_logo from '@/assets/image/me_logo.png'
import no_pic from '@/assets/image/no_pic.png'
import dayjs from 'dayjs'

/**
 * 节流函数
 * @param fn 函数
 * @param delay 延迟时间
 * @returns
 */
export const throttle = <T extends (...args: unknown[]) => void>(fn: T, delay: number) => {
  let lastTime = 0
  return (...args: Parameters<T>): void => {
    const now = Date.now()
    if (now - lastTime >= delay) {
      fn(...args)
      lastTime = now
    }
  }
}

/**
 * 防抖函数
 * @param fn 函数
 * @param delay 延迟时间
 * @returns
 */
export const debounce = <T extends (...args: unknown[]) => void>(fn: T, delay: number) => {
  let timeout: any
  return (...args: Parameters<T>): void => {
    clearTimeout(timeout)
    timeout = setTimeout(() => fn(...args), delay)
  }
}

/**
 * 判断值是否为空或为空字符串
 * @param value 值
 * @returns 是否为空或为空字符串
 */
export function isEmptyOrNil(value: any): boolean {
  return value === null || value === undefined || value === ''
}

/**
 * 截取字符串
 * @param val 字符串
 * @param startLength 开始长度
 * @param endLength 结束长度
 * @returns 截取后的字符串
 */
export const cutStr = (val: string, startLength = 6, endLength = 6) => {
  if (val?.length < startLength + endLength) return val
  return `${val?.slice(0, startLength)}...${val?.slice(0 - endLength)}`
}

/**
 * 获取国家旗帜 URL
 * @param regionName 区域名称
 * @returns 国家旗帜 URL
 */
export const getCountryFlagUrl = (regionName: string | null | undefined) => {
  if (!regionName) return { url: '-', regionId: '-' }
  const upperRegionName = regionName.toUpperCase()
  const specialRegions: Record<string, { url: string; regionId: string }> = {
    EXPERIENCE_REGION: { url: no_pic, regionId: 'EXPERIENCE' },
    ME_EARTH: { url: me_logo, regionId: 'ME_EARTH' },
  }
  // 如果是特殊区域，直接返回对应的结果
  if (specialRegions[upperRegionName]) {
    return specialRegions[upperRegionName]
  }
  // 默认返回公共旗帜 URL
  return {
    url: `https://public.mehp.world/flags/small/${upperRegionName}.png`,
    regionId: upperRegionName,
  }
}

export const formatTimeAgo = (timeLong: number) => {
  //计算出相差天数
  const days = Math.floor(timeLong / (24 * 3600))
  //计算出小时数
  const leave1 = timeLong % (24 * 3600) //计算天数后剩余的毫秒数
  const hours = Math.floor(leave1 / 3600)
  //计算相差分钟数
  const leave2 = leave1 % 3600 //计算小时数后剩余的毫秒数
  const minutes = Math.floor(leave2 / 60)
  //计算相差秒数
  const leave3 = leave2 % 60 //计算分钟数后剩余的毫秒数
  const seconds = Math.round(leave3)

  if (timeLong < 60) return `${timeLong}&_s`

  if (timeLong == 60) return `${minutes}&_m`

  if (timeLong > 60 && timeLong < 3600) return `${minutes}&_m${seconds}&_s`

  if (timeLong < 3600) return `${hours}&_h${minutes}&_m`

  if (timeLong == 3600) return `${hours}&_h`

  if (days < 1) return `${hours}&_h${minutes}&_m`

  if (days == 1) return `${days}&_d`

  return `${days}&_d${hours}&_h`
}

export function getFormatTimeZone(
  date: string | number | Date,
  zone?: number,
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  //@ts-expect-error
  formatType?: string = 'YYYY-MM-DD HH:mm:ss',
) {
  if (!date) return { timeUtc0: '-', currentTime: '-', currentUtc0: '-' }
  /*const zoneTime = zone ? zone : new Date().getTimezoneOffset() / 60
  if (zoneTime > 0) {
    return dayjs(date).add(zoneTime, 'hour').format(formatType)
  }
  return dayjs(date).subtract(zoneTime, 'hour').format(formatType)*/
  // 当前时区
  const INIT_TIME_ZONE = (0 - new Date().getTimezoneOffset()) / 60
  // console.log('当前时区', INIT_TIME_ZONE)

  // UTC+0 时间
  const timeUtc0 = dayjs.utc(date).format(formatType).toString()
  // 当前时间
  const currentTime = dayjs.utc(date).add(INIT_TIME_ZONE, 'hour').format(formatType)

  // 当前时间转UTC-0
  const currentUtc0 = dayjs().utc().format('YYYY-MM-DDTHH:mm:ss')
  // console.log('当前时间转UTC-0', currentUtc0)

  return {
    timeUtc0,
    currentTime,
    currentUtc0,
  }
}

/**
 * 去除字符串中默认的0或者小数点
 * @param string
 * @returns string
 */
export function removeDefaultZero(val: string) {
  if (!val) return '-'

  // 将数字转换为字符串
  let str = val

  // 如果包含小数点
  if (str.indexOf('.') !== -1) {
    // 移除末尾的零
    str = str.replace(/0+$/, '')
    // 如果小数点后没有数字了，也移除小数点
    str = str.replace(/\.$/, '')
  }
  return str
}

export const getLeftKuohao = (curReponsive: any) => {
  if (curReponsive?.size === 'mobile') {
    return '('
  }
  return '（'
}

export const getRightKuohao = (curReponsive: any) => {
  if (curReponsive?.size === 'mobile') {
    return ')'
  }
  return '）'
}

export const navigateQueryExistsFn = async (searchValue: string | undefined, navigate: any) => {
  if (!searchValue) return
  const res = await queryExists(searchValue)
  if (res?.existence_check?.length) {
    const result = res.existence_check[0]
    switch (result.search_type) {
      case 'account': // 普通地址
        navigate(`/accountDetail/${searchValue}`)
        break
      case 'module_account': // 模块地址
        navigate(`/moduleAccount/${searchValue}`)
        break
      // hub层交易
      case 'hub_transaction': // 交易
        navigate(`/transaction/${searchValue}?net_type=hub`)
        break
      // rollup层交易
      case 'checkin_transaction': // 签到交易
        navigate(`/transaction/${searchValue}?net_type=rollup`)
        break
    }
  }
}

/**
 * PROPOSAL_STATUS_UNSPECIFIED
 *   未指定状态，表示默认或未知状态
 * PROPOSAL_STATUS_DEPOSIT_PERIOD
 *   提案处于存款期
 *   此阶段需要达到最低存款金额才能进入投票期
 *   如果在规定时间内没有达到最低存款要求，提案将被丢弃
 * PROPOSAL_STATUS_VOTING_PERIOD
 *   提案已收集足够存款并进入投票阶段
 *   在此期间，验证人和委托人可以对提案进行投票
 * PROPOSAL_STATUS_PASSED
 *   提案通过
 *   表示提案获得了足够的"是"票，满足了通过阈值
 * PROPOSAL_STATUS_REJECTED
 *   提案被拒绝
 *   可能是因为未达到法定人数要求或其他技术原因导致的失败
 * PROPOSAL_STATUS_FAILED
 *   提案失败
 *   可能是因为未达到法定人数要求或其他技术原因导致的失败
 * @param {string} status 提案状态
 * return {string} 转换后提案状态
 */
export const getStatus = (status: string, t: any) => {
  let statusText = ''
  switch (status) {
    case 'PROPOSAL_STATUS_DEPOSIT_PERIOD':
    case 'PROPOSAL_STATUS_VOTING_PERIOD':
      statusText = t('explorer_272')
      break
    case 'PROPOSAL_STATUS_PASSED':
      statusText = t('explorer_273')
      break
    case 'PROPOSAL_STATUS_REJECTED':
    case 'PROPOSAL_STATUS_FAILED':
      statusText = t('explorer_274')
      break
    default:
      statusText = '-'
      break
  }
  return statusText
}

/**
 * 获取节点名称
 * @param moniker 节点名称
 * @returns 节点名称
 */
export const getMoniker = (moniker: string) => {
  const moniker_text = moniker.replace('-node', '').toLocaleLowerCase()
  const obj = {
    global: 'Node-1',
    chn: 'Node-2',
    usa: 'Node-3',
    ind: 'Node-4',
  }
  console.log('moniker_text', moniker_text)

  return obj[moniker_text as keyof typeof obj] || moniker
}
